import React, { useState, useCallback } from 'react';
import Stats from './Stats';
import Newsfeed from './Newsfeed';
import Feedback from './Feedback';
import Puzzles from './Puzzles';

const AdminDashboard: React.FC = () => {
    // Initialize activeTab from query string or default to 'puzzles'
    const getInitialTab = (): 'puzzles' | 'stats' | 'newsfeed' | 'feedback' => {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam && ['puzzles', 'stats', 'newsfeed', 'feedback'].includes(tabParam)) {
            return tabParam as 'puzzles' | 'stats' | 'newsfeed' | 'feedback';
        }
        return 'puzzles';
    };

    const [activeTab, setActiveTab] = useState<'puzzles' | 'stats' | 'newsfeed' | 'feedback'>(
        getInitialTab
    );
    const [error, setError] = useState<string | null>(null);

    // Function to update the query string when tab changes
    const updateQueryString = (tab: 'puzzles' | 'stats' | 'newsfeed' | 'feedback') => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', tab);
        window.history.replaceState({}, '', url.toString());
    };

    // Function to handle tab changes
    const handleTabChange = (tab: 'puzzles' | 'stats' | 'newsfeed' | 'feedback') => {
        setActiveTab(tab);
        updateQueryString(tab);
    };

    const handleError = useCallback((errorMessage: string) => {
        setError(errorMessage);
    }, []);

    return (
        <div className='min-h-screen bg-gray-50'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
                {error && (
                    <div className='mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded'>
                        {error}
                    </div>
                )}

                <div className='mb-6'>
                    <nav className='flex space-x-8'>
                        <button
                            onClick={() => handleTabChange('puzzles')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'puzzles'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Puzzles
                        </button>
                        <button
                            onClick={() => handleTabChange('stats')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'stats'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Statistics
                        </button>
                        <button
                            onClick={() => handleTabChange('newsfeed')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'newsfeed'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Newsfeed
                        </button>
                        <button
                            onClick={() => handleTabChange('feedback')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'feedback'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Feedback
                        </button>
                    </nav>
                </div>

                {activeTab === 'puzzles' && <Puzzles onError={handleError} />}
                {activeTab === 'stats' && <Stats onError={handleError} />}
                {activeTab === 'newsfeed' && <Newsfeed onError={handleError} />}
                {activeTab === 'feedback' && <Feedback onError={handleError} />}
            </div>
        </div>
    );
};

export default AdminDashboard;
